/**
 * BidResponse — the no-login page a contractor lands on from the bid email.
 *
 * Shows the job scope + offered labor amount, and lets them Accept, Counter
 * once, or Decline. Token-keyed (no auth). After a response, the page reflects
 * the new state. See .kiro/specs/contractor-bids.md.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getBid, acceptBid, counterBid, declineBid, type BidView } from '../lib/api';

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function BidResponse(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [bid, setBid] = useState<BidView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [countering, setCountering] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');

  useEffect(() => {
    if (!token) return;
    getBid(token)
      .then(setBid)
      .catch(() => setError('We couldn\u2019t find this offer. The link may be invalid or expired.'));
  }, [token]);

  async function act(fn: () => Promise<{ status: string }>): Promise<void> {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setBid(await getBid(token));
      setCountering(false);
    } catch {
      setError('That didn\u2019t go through. The offer may have expired or already been answered.');
    } finally {
      setBusy(false);
    }
  }

  if (error && !bid) {
    return (
      <div className="flow flow--single"><div className="stage-card">
        <div className="pb-note pb-note--flag" role="alert">{error}</div>
      </div></div>
    );
  }
  if (!bid) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Loading&hellip;</p></div></div>;
  }

  const statusMessage: Record<string, string> = {
    ACCEPTED_BY_CONTRACTOR: 'You accepted this offer. We\u2019ll confirm and be in touch.',
    COUNTERED: `You countered at ${bid.counterAmountCents != null ? dollars(bid.counterAmountCents) : ''}. We\u2019ll review and get back to you.`,
    DECLINED: 'You declined this offer. Thanks for letting us know.',
    ASSIGNED: 'This job has been assigned to you. We\u2019ll be in touch with the details.',
    EXPIRED: 'This offer has expired.',
    CLOSED_OTHER_ASSIGNED: 'This job has been assigned to another contractor.',
  };

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <div className="progress__count">JOB OFFER</div>
        <h1>{bid.projectName ?? 'A job for you'}</h1>
        <p className="stage-card__lede">Hi {bid.contractorName} — here&rsquo;s the work and our offer.</p>

        <div className="estimate estimate--hero" style={{ marginBottom: 'var(--pb-3)' }}>
          <div className="estimate__label">OFFERED FOR THE LABOR</div>
          <div className="estimate__value">{dollars(bid.offeredAmountCents)}</div>
        </div>

        <div className="pb-surface" style={{ marginBottom: 'var(--pb-3)' }}>
          <div className="pb-option__label" style={{ marginBottom: 'var(--pb-2)' }}>Scope</div>
          <ul style={{ margin: 0, paddingLeft: 'var(--pb-4)' }}>
            {bid.roomLabels.map((l, i) => (
              <li key={i} style={{ fontSize: 'var(--pb-size-small)', padding: '2px 0' }}>{l}</li>
            ))}
          </ul>
        </div>

        {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

        {bid.canRespond ? (
          countering ? (
            <div className="pb-surface">
              <label className="pb-label" htmlFor="counter">Your counter (labor total)</label>
              <input
                id="counter"
                className="pb-field"
                inputMode="decimal"
                placeholder={(bid.offeredAmountCents / 100).toFixed(2)}
                value={counterAmount}
                onChange={(e) => setCounterAmount(e.target.value)}
                style={{ marginBottom: 'var(--pb-2)' }}
              />
              <div className="q-actions" style={{ flexDirection: 'column' }}>
                <button
                  type="button"
                  className="pb-btn pb-btn--lg"
                  disabled={busy || !counterAmount.trim() || Number.isNaN(Number(counterAmount))}
                  onClick={() => void act(() => counterBid(token as string, Math.round(Number(counterAmount) * 100)))}
                >
                  {busy ? 'Sending\u2026' : 'Send counter'}
                </button>
                <button type="button" className="pb-btn pb-btn--ghost" onClick={() => setCountering(false)} disabled={busy}>
                  Back
                </button>
              </div>
              <p className="estimate__hint" style={{ marginTop: 'var(--pb-2)' }}>You can counter once.</p>
            </div>
          ) : (
            <div className="q-actions" style={{ flexDirection: 'column' }}>
              <button type="button" className="pb-btn pb-btn--lg" disabled={busy} onClick={() => void act(() => acceptBid(token as string))}>
                Accept {dollars(bid.offeredAmountCents)}
              </button>
              <button type="button" className="pb-btn pb-btn--ghost pb-btn--lg" onClick={() => setCountering(true)} disabled={busy}>
                Counter
              </button>
              <button type="button" className="pb-btn pb-btn--ghost" disabled={busy} onClick={() => void act(() => declineBid(token as string))}>
                Decline
              </button>
            </div>
          )
        ) : (
          <div className="pb-note pb-note--good" role="status">
            {statusMessage[bid.status] ?? `Status: ${bid.status}`}
          </div>
        )}
      </div>
    </div>
  );
}
